// =============================================
// GLOSSARY DATA & RENDERING — V2
// Organized by pathway phases with Mental Hooks
// Spatial Association (Method of Loci)
// =============================================

// Phase categories for spatial grouping
export const GLOSSARY_PHASES = [
    { id: 'air-pathway', name: 'Phase 1: The Air Pathway', icon: '💨', desc: 'The Lungs — how air enters and flows', cameraTarget: 'venturi' },
    { id: 'fuel-supply', name: 'Phase 2: The Fuel Supply', icon: '⛽', desc: 'Tank to the bowl — fuel delivery', cameraTarget: 'float-bowl' },
    { id: 'mixing-zone', name: 'Phase 3: The Mixing Zone', icon: '🔬', desc: 'The chemistry — metering & atomization', cameraTarget: 'discharge-nozzle' },
    { id: 'adapters', name: 'Phase 4: The Adapters', icon: '🎛️', desc: 'Reacting to pilot & environment', cameraTarget: 'mixture-control' },
    { id: 'hazards', name: 'Phase 5: Hazards & Abnormalities', icon: '⚠️', desc: 'Troubleshooting & failure modes', cameraTarget: 'throttle-body' },
];

export const GLOSSARY_TERMS = [
    // ========== PHASE 1: THE AIR PATHWAY (The Lungs) ==========
    {
        term: 'Atmospheric Pressure',
        definition: 'The weight of the air pushing down on the earth. At sea level, it is approximately 14.7 psi (29.92 in. Hg). This force acts on the fuel in the float bowl and is the "push" that moves fuel toward the low-pressure venturi.',
        mentalHook: '🖐️ The "Invisible Hand." It\'s the force that literally pushes fuel out of the float bowl when the venturi creates a vacuum.',
        phase: 'air-pathway',
        category: 'air',
        relatedPart: 'float-bowl'
    },
    {
        term: 'Venturi',
        definition: 'A specially shaped restriction (convergent-divergent nozzle) in the carburetor throat that speeds up airflow and drops static pressure. The venturi is where the main discharge nozzle sits, exploiting the pressure differential to draw fuel.',
        mentalHook: '⏳ The "Hourglass." Squeeze the air, it panics, speeds up, and drops its pressure.',
        phase: 'air-pathway',
        category: 'air',
        animation: 'venturi',
        relatedPart: 'venturi'
    },
    {
        term: 'Bernoulli\'s Principle',
        definition: 'The physical law stating that as the velocity of a fluid (or gas) increases, its internal (static) pressure decreases. This is the fundamental operating principle of every carburetor — fast air in the venturi = low pressure = fuel gets drawn in.',
        mentalHook: '⚡ "Fast equals low." Fast air = low pressure. Period.',
        phase: 'air-pathway',
        category: 'air',
    },
    {
        term: 'Throttle Valve (Butterfly Valve)',
        definition: 'A rotating disk in the carburetor throat that regulates the total volume of the fuel/air mixture entering the engine. It does NOT just control fuel — it controls the entire charge entering the cylinders. Linked to the throttle lever in the cockpit.',
        mentalHook: '🚪 The "Breathing Door." It doesn\'t control just fuel; it controls the whole mixture going to the cylinders.',
        phase: 'air-pathway',
        category: 'air',
        relatedPart: 'throttle-valve'
    },
    {
        term: 'Impact Air',
        definition: 'Air taken in via the forward-facing scoop, rammed into the induction system by the aircraft\'s forward speed. In pressure-injection carburetors (e.g., Bendix PS-5C), impact air is directed to Chamber A to provide air metering force.',
        mentalHook: '🏎️ "Ramming Speed Air." Used in pressure-injection systems (Chamber A) to measure airflow.',
        phase: 'air-pathway',
        category: 'air',
        relatedPart: 'air-inlet'
    },

    // ========== PHASE 2: THE FUEL SUPPLY (Tank to the Bowl) ==========
    {
        term: 'Volatility',
        definition: 'The tendency of a liquid to vaporize (turn into a gas). Aviation fuel needs high volatility to start cold engines and vaporize properly in the carburetor, but not so high that it boils in the lines causing vapor lock.',
        mentalHook: '🌡️ "Eagerness to evaporate." The fuel\'s desire to become a gas. Too eager → vapor lock. Not eager enough → hard cold starts.',
        phase: 'fuel-supply',
        category: 'fuel',
    },
    {
        term: 'Reid Vapor Pressure (RVP)',
        definition: 'A standard laboratory measurement of a fuel\'s volatility at 100°F (37.8°C). Higher RVP means the fuel vaporizes more easily. Aviation gasoline has a carefully controlled RVP to balance starting performance against vapor lock resistance.',
        mentalHook: '🧪 The "Boil Test." How easily fuel turns to gas at a standard hot temperature.',
        phase: 'fuel-supply',
        category: 'fuel',
    },
    {
        term: 'Vapor Lock',
        definition: 'A condition where fuel vaporizes in the fuel lines before reaching the carburetor, blocking the flow of liquid fuel. The fuel pump tries to push liquid, but compresses a gas bubble instead, starving the engine. Common in hot weather, at high altitude, and near exhaust-heated lines.',
        mentalHook: '👻 "Ghost Fuel." The pump tries to push liquid, but just compresses a ghost (gas bubble), starving the engine.',
        phase: 'fuel-supply',
        category: 'fuel',
    },
    {
        term: 'Float Chamber (Bowl)',
        definition: 'A small reservoir inside the carburetor that holds a constant level of unmetered fuel. It is vented to atmospheric pressure. The float rises and falls with fuel level, actuating the needle valve to control fuel inlet flow. The precise level is critical — too high floods, too low starves.',
        mentalHook: '🚽 "The Toilet Tank." It holds water (fuel) at a steady level until it\'s needed. Same mechanism, different fluid.',
        phase: 'fuel-supply',
        category: 'fuel',
        animation: 'float-level',
        relatedPart: 'float-bowl'
    },
    {
        term: 'Needle Valve & Seat',
        definition: 'The mechanism that opens and closes the fuel inlet to the float chamber, actuated by the rising and falling float. When the bowl is full, the float rises and pushes the needle into the seat, sealing off fuel flow. When fuel level drops, the needle opens.',
        mentalHook: '🚧 The "Bouncer." Only lets fuel in when the bowl is getting empty.',
        phase: 'fuel-supply',
        category: 'fuel',
        relatedPart: 'needle-valve'
    },
    {
        term: 'Fuel Starvation vs. Fuel Exhaustion',
        definition: 'Fuel starvation means there IS fuel onboard, but it cannot reach the engine — due to a closed selector valve, blocked line, vapor lock, or vent blockage. Fuel exhaustion means the aircraft has actually run out of fuel. Both cause engine stoppage, but starvation is a system or procedural fault, while exhaustion is a fuel management failure.',
        mentalHook: '🔑 "Locked pantry vs. empty pantry." Food exists but can\'t be reached, versus no food at all.',
        phase: 'fuel-supply',
        category: 'fuel',
    },

    // ========== PHASE 3: THE MIXING ZONE (The Chemistry) ==========
    {
        term: 'Main Metering Jet',
        definition: 'A precisely calibrated orifice that limits the maximum amount of fuel that can be drawn from the float chamber into the main discharge system. In the MA-3/MA-4 series, these are plain tube, fixed jets — the orifice is physically drilled at the factory and cannot be field-adjusted.',
        mentalHook: '🎫 "The Strict Turnstile." It restricts the crowd (fuel) so only a mathematically precise amount gets through.',
        phase: 'mixing-zone',
        category: 'mixture',
        animation: 'main',
        relatedPart: 'main-jet'
    },
    {
        term: 'Discharge Nozzle',
        definition: 'The tube sitting in the narrowest part of the venturi where fuel exits into the airstream. The low pressure created by the venturi draws fuel up through this nozzle. Its tip is positioned at the point of maximum velocity / minimum pressure.',
        mentalHook: '🥤 "The Straw." The low pressure in the venturi literally sucks fuel up this straw.',
        phase: 'mixing-zone',
        category: 'mixture',
        relatedPart: 'discharge-nozzle'
    },
    {
        term: 'Surface Tension',
        definition: 'The cohesive force of liquid molecules holding together at the surface, causing fuel to come out in big, messy drops rather than fine mist. Surface tension must be broken for proper atomization and combustion.',
        mentalHook: '🧲 "Liquid Magnetism." Fuel molecules stick together like tiny magnets. Air bleeds break them apart.',
        phase: 'mixing-zone',
        category: 'mixture',
    },
    {
        term: 'Air Bleed & Emulsion',
        definition: 'A small calibrated passage that introduces air into the fuel before it leaves the discharge nozzle, breaking surface tension. The resulting fuel-air emulsion atomizes much better when it exits into the airstream. Air bleeds also serve a compensating function at higher airspeeds.',
        mentalHook: '☕ "The Frother." Like a barista steaming milk, it turns liquid fuel into a fine, easily burned mist.',
        phase: 'mixing-zone',
        category: 'air',
        animation: 'bleed',
        relatedPart: 'air-bleed'
    },
    {
        term: 'Stoichiometric Mixture (Ratio)',
        definition: 'The chemically perfect ratio of air to fuel where all fuel and all oxygen are completely consumed in combustion. For aviation gasoline, approximately 15 pounds of air to 1 pound of fuel (15:1 by weight).',
        mentalHook: '💍 "The Perfect 15:1 Marriage." 15 pounds of air to exactly 1 pound of fuel. Every molecule finds its partner.',
        phase: 'mixing-zone',
        category: 'mixture',
    },
    {
        term: 'Best Power Mixture',
        definition: 'The fuel-air ratio that produces maximum power output — typically about 12.5:1 (slightly rich of stoichiometric). Used for takeoff and climb. The extra fuel beyond peak efficiency provides cooling and a margin against detonation.',
        mentalHook: '💪 "Sprint Mode." Slightly rich for maximum muscle, with extra fuel as coolant.',
        phase: 'mixing-zone',
        category: 'mixture',
    },
    {
        term: 'Best Economy Mixture',
        definition: 'The leanest mixture that still produces smooth engine operation and acceptable power — typically around 16:1 to 16.5:1. Used during cruise to minimize fuel consumption. Identified by leaning to peak EGT, then enriching slightly (~50°F rich of peak).',
        mentalHook: '🏦 "Marathon Mode." Maximum miles per gallon. Lean as you dare without burning things up.',
        phase: 'mixing-zone',
        category: 'mixture',
    },
    {
        term: 'Specific Fuel Consumption (SFC)',
        definition: 'The number of pounds of fuel burned per hour to produce one horsepower. Formula: SFC = Fuel Flow (lbs/hr) ÷ Horsepower. Lower SFC means a more efficient engine. Typical values: 0.4 to 0.5 lbs/hp/hr for reciprocating engines.',
        mentalHook: '🍔 "Engine Diet." How many calories (pounds of fuel) it takes to do one unit of work.',
        phase: 'mixing-zone',
        category: 'mixture',
    },
    {
        term: 'Mixture Strength: Rich & Lean',
        definition: 'Rich means more fuel relative to air than stoichiometric. Lean means less fuel. Rich runs cooler, wastes fuel, fouls spark plugs with carbon. Lean runs hotter but more efficiently — until detonation territory.',
        mentalHook: '🔥 Rich = "Drowning the fire" (cool, smoky). Lean = "Starving the fire" (hot, dangerous).',
        phase: 'mixing-zone',
        category: 'mixture',
        animation: 'rich-lean',
    },
    {
        term: 'Idle System',
        definition: 'At idle, the throttle plate is nearly closed and venturi pressure drop is too weak to draw fuel. Instead, the high manifold vacuum downstream of the throttle plate acts on the idle discharge port, drawing fuel through a separate idle passage with its own metering restriction and air bleed.',
        mentalHook: '🕳️ "The Back Door." When the main entrance (venturi) is closed for business, fuel sneaks in through the side.',
        phase: 'mixing-zone',
        category: 'mixture',
        animation: 'idle',
    },
    {
        term: 'Transition Ports (Progression Holes)',
        definition: 'Small holes in the carburetor bore between the idle port and the main discharge area. As the throttle opens from idle, these holes are progressively exposed to intake vacuum, providing a smooth handoff from idle to main metering.',
        mentalHook: '🌉 "The Bridge." Smooth handoff from idle fuel to main fuel so the engine doesn\'t stumble.',
        phase: 'mixing-zone',
        category: 'mixture',
        animation: 'transition'
    },

    // ========== PHASE 4: THE ADAPTERS (Reacting to Pilot & Environment) ==========
    {
        term: 'Accelerator Pump',
        definition: 'A mechanical pump linked to the throttle that squirts a raw shot of fuel into the throat during rapid throttle advancement. Compensates for the momentary lean condition when airflow increases faster than fuel flow during sudden acceleration.',
        mentalHook: '☕ "The Espresso Shot." When the throttle slams open, air rushes in instantly, but fuel lags. This pump gives it the sudden jolt of energy it needs to prevent a stumble.',
        phase: 'adapters',
        category: 'fuel',
        relatedPart: 'accelerator-pump'
    },
    {
        term: 'Economizer Valve (Power Enrichment System)',
        definition: 'A valve that opens at high throttle settings (typically above 60-70% power) to supply extra fuel beyond what the main metering jet provides. Despite its name, it adds fuel — the extra, unburned fuel absorbs heat and cools the cylinders to prevent detonation at high power.',
        mentalHook: '🧊 "Liquid Coolant." It\'s called an economizer, but it ADDS extra fuel at high power. The unburned fuel absorbs heat and cools the cylinders to prevent melting.',
        phase: 'adapters',
        category: 'fuel',
        relatedPart: 'economizer'
    },
    {
        term: 'Automatic Mixture Control (AMC)',
        definition: 'A sealed bellows (aneroid) unit that expands and contracts with altitude changes to automatically lean or enrich the mixture. As the aircraft climbs and air density decreases, the bellows expands and reduces fuel flow proportionally.',
        mentalHook: '🫁 "The Altitude Lung." As the plane climbs, the air gets thin. The AMC lung expands and chokes off some fuel to keep the mixture balanced.',
        phase: 'adapters',
        category: 'control',
        relatedPart: 'mixture-control'
    },
    {
        term: 'Back-Suction Mixture Control',
        definition: 'A type of mixture control that reduces the pressure inside the float bowl by connecting it to a low-pressure area. This decreases the pressure differential across the main jet, reducing fuel flow and leaning the mixture — without physically blocking fuel.',
        mentalHook: '🤜🤛 "The Vacuum Tug-of-War." Instead of blocking fuel, it reduces the push behind it by letting vacuum into the bowl.',
        phase: 'adapters',
        category: 'control',
    },

    // ========== PHASE 5: HAZARDS & ABNORMALITIES (Troubleshooting) ==========
    {
        term: 'Carburetor Icing',
        definition: 'The formation of ice in the carburetor throat due to the massive temperature drop caused by fuel vaporization (latent heat) and venturi pressure drops (adiabatic cooling). Can occur even on warm, humid days (20-70°F ambient). Most dangerous at reduced power settings.',
        mentalHook: '🧊 "The Invisible Freezer." Even on a warm 70°F day, the inside of a carburetor can drop below freezing and choke the engine with ice.',
        phase: 'hazards',
        category: 'air',
    },
    {
        term: 'Detonation',
        definition: 'An uncontrolled, explosive burning of the fuel/air mixture inside the cylinder, rather than a smooth, progressive burn. The remaining unburned charge self-ignites from heat and pressure, creating destructive pressure waves. Causes: lean mixture, low octane, high MAP, high CHT.',
        mentalHook: '🔨 "The Hammer Blow." Instead of a firm push on the piston, it explodes like dynamite, destroying engine parts.',
        phase: 'hazards',
        category: 'fuel',
        animation: 'detonation'
    },
    {
        term: 'Pre-ignition',
        definition: 'When the fuel/air mixture ignites BEFORE the spark plug fires, usually caused by a glowing hot carbon deposit, overheated spark plug electrode, or sharp valve edge. Creates extreme stress as the piston fights against premature combustion.',
        mentalHook: '🏁 "Jumping the Gun." The fire starts before the referee (spark plug) says go.',
        phase: 'hazards',
        category: 'fuel',
    },
    {
        term: 'Rich Mixture (Symptoms)',
        definition: 'A mixture containing more fuel than necessary. Symptoms: rough running, black exhaust smoke, fouled spark plugs (carbon), reduced power, elevated fuel consumption. Runs cooler than lean but wastes fuel.',
        mentalHook: '🌊 "Drowning the Fire." Runs cool, wastes fuel, fouls spark plugs with black soot.',
        phase: 'hazards',
        category: 'mixture',
    },
    {
        term: 'Lean Mixture (Symptoms)',
        definition: 'A mixture containing more air than necessary. Symptoms: rough running, backfiring in the intake, high cylinder head temperatures, risk of detonation, burned exhaust valves. Runs excessively hot.',
        mentalHook: '🔥 "Starving the Fire." Runs excessively hot, causes detonation, burns up valves.',
        phase: 'hazards',
        category: 'mixture',
    },
    {
        term: 'Float Chamber Level & Reference Pressure',
        definition: 'The float bowl maintains fuel at a precise level. The bowl is vented to atmospheric pressure, creating a known reference. Fuel flows from the bowl to the discharge nozzle only because the venturi creates a lower pressure at the nozzle tip.',
        mentalHook: '📏 "The Baseline." Everything in the carburetor is measured against this steady fuel level and its atmospheric pressure reference.',
        phase: 'fuel-supply',
        category: 'fuel',
        animation: 'float-level',
    },
];

// ========== RENDERING ==========

export function renderGlossary(container, filter = '', phaseFilter = '') {
    container.innerHTML = '';
    let terms = GLOSSARY_TERMS;

    if (phaseFilter) {
        terms = terms.filter(t => t.phase === phaseFilter);
    }

    if (filter) {
        const f = filter.toLowerCase();
        terms = terms.filter(t =>
            t.term.toLowerCase().includes(f) ||
            t.definition.toLowerCase().includes(f) ||
            (t.mentalHook && t.mentalHook.toLowerCase().includes(f)));
    }

    // Group by phase
    const phases = {};
    terms.forEach(t => {
        if (!phases[t.phase]) phases[t.phase] = [];
        phases[t.phase].push(t);
    });

    const phaseOrder = ['air-pathway', 'fuel-supply', 'mixing-zone', 'adapters', 'hazards'];
    const phaseNames = {
        'air-pathway': '💨 Phase 1: The Air Pathway',
        'fuel-supply': '⛽ Phase 2: Fuel Supply',
        'mixing-zone': '🔬 Phase 3: The Mixing Zone',
        'adapters': '🎛️ Phase 4: The Adapters',
        'hazards': '⚠️ Phase 5: Hazards & Abnormalities',
    };

    phaseOrder.forEach(phase => {
        if (!phases[phase] || phases[phase].length === 0) return;

        const section = document.createElement('div');
        section.className = 'glossary-phase-section';

        const heading = document.createElement('div');
        heading.className = 'glossary-phase-heading';
        heading.textContent = phaseNames[phase] || phase;
        section.appendChild(heading);

        const grid = document.createElement('div');
        grid.className = 'glossary-grid';

        phases[phase].forEach(item => {
            const card = document.createElement('div');
            card.className = 'glossary-card animate-in';

            const categoryColors = {
                air: '#4fc3f7',
                fuel: '#c62828',
                mixture: '#ff9800',
                control: '#66bb6a'
            };

            card.innerHTML = `
                <h4 style="color:${categoryColors[item.category] || '#94a3b8'}">${item.term}</h4>
                <p>${item.definition}</p>
                ${item.mentalHook ? `<div class="mental-hook"><span class="hook-label">🧠 Mental Hook:</span> ${item.mentalHook}</div>` : ''}
                ${item.animation ? `<canvas class="glossary-anim" data-anim="${item.animation}" width="300" height="80"></canvas>` : ''}
            `;
            grid.appendChild(card);
        });

        section.appendChild(grid);
        container.appendChild(section);
    });

    // Draw mini animations
    document.querySelectorAll('.glossary-anim').forEach(canvas => {
        drawGlossaryAnimation(canvas, canvas.dataset.anim);
    });
}

function drawGlossaryAnimation(canvas, type) {
    const ctx = canvas.getContext('2d');
    const w = canvas.width;
    const h = canvas.height;

    ctx.fillStyle = '#111827';
    ctx.fillRect(0, 0, w, h);

    switch (type) {
        case 'venturi':
            drawVenturiDiagram(ctx, w, h);
            break;
        case 'float-level':
            drawFloatDiagram(ctx, w, h);
            break;
        case 'idle':
            drawIdleDiagram(ctx, w, h);
            break;
        case 'main':
            drawMainDiagram(ctx, w, h);
            break;
        case 'rich-lean':
            drawRichLeanDiagram(ctx, w, h);
            break;
        case 'bleed':
            drawBleedDiagram(ctx, w, h);
            break;
        default:
            ctx.fillStyle = '#333';
            ctx.font = '10px Inter';
            ctx.fillText('Visual diagram', 10, h / 2);
    }
}

function drawVenturiDiagram(ctx, w, h) {
    ctx.strokeStyle = '#4fc3f7';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(20, 15);
    ctx.quadraticCurveTo(w / 2, 30, w - 20, 15);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(20, h - 15);
    ctx.quadraticCurveTo(w / 2, h - 30, w - 20, h - 15);
    ctx.stroke();

    ctx.strokeStyle = 'rgba(79,195,247,.5)';
    ctx.lineWidth = 1;
    for (let i = 0; i < 5; i++) {
        const x = 40 + i * (w - 80) / 4;
        ctx.beginPath();
        ctx.moveTo(x, h / 2 - 5);
        ctx.lineTo(x + 15, h / 2);
        ctx.lineTo(x, h / 2 + 5);
        ctx.stroke();
    }

    ctx.fillStyle = '#94a3b8';
    ctx.font = '9px Inter';
    ctx.fillText('High P', 30, h / 2 - 10);
    ctx.fillStyle = '#3b82f6';
    ctx.fillText('Low P', w / 2 - 15, h / 2 - 10);
    ctx.fillStyle = '#94a3b8';
    ctx.fillText('High P', w - 60, h / 2 - 10);
}

function drawFloatDiagram(ctx, w, h) {
    ctx.strokeStyle = '#5a6577';
    ctx.lineWidth = 2;
    ctx.strokeRect(w / 4, 15, w / 2, h - 30);

    ctx.fillStyle = 'rgba(198,40,40,.3)';
    ctx.fillRect(w / 4 + 2, h / 2, w / 2 - 4, h / 2 - 17);

    ctx.fillStyle = '#fdd835';
    ctx.fillRect(w / 3, h / 2 - 3, w / 6, 8);

    ctx.fillStyle = '#c62828';
    ctx.beginPath();
    ctx.moveTo(w / 2, 15);
    ctx.lineTo(w / 2 - 4, h / 2 - 8);
    ctx.lineTo(w / 2 + 4, h / 2 - 8);
    ctx.fill();

    ctx.fillStyle = '#94a3b8';
    ctx.font = '9px Inter';
    ctx.fillText('ATM', w * 3 / 4 + 5, 25);
    ctx.fillText('Fuel level', w / 4 + 5, h / 2 + 20);
}

function drawIdleDiagram(ctx, w, h) {
    ctx.fillStyle = '#94a3b8';
    ctx.font = '9px Inter';
    ctx.fillText('Throttle nearly closed', 10, 12);

    ctx.strokeStyle = '#66bb6a';
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.moveTo(w / 2 - 5, 20);
    ctx.lineTo(w / 2 + 5, h - 20);
    ctx.stroke();

    ctx.fillStyle = '#ff9800';
    ctx.beginPath();
    ctx.arc(w / 2 + 15, h - 25, 4, 0, Math.PI * 2);
    ctx.fill();

    ctx.fillStyle = '#94a3b8';
    ctx.fillText('Idle port', w / 2 + 25, h - 22);
    ctx.fillText('High vacuum here', w / 2 + 25, h - 10);
}

function drawMainDiagram(ctx, w, h) {
    ctx.fillStyle = '#94a3b8';
    ctx.font = '9px Inter';
    ctx.fillText('Bowl → Main Jet → Air Bleed → Nozzle → Venturi', 10, 12);

    const points = [[30, h - 15], [80, h - 15], [80, h / 2], [150, h / 2], [150, 25], [w - 30, 25]];
    ctx.strokeStyle = '#c62828';
    ctx.lineWidth = 2;
    ctx.beginPath();
    points.forEach(([x, y], i) => i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y));
    ctx.stroke();

    ctx.fillStyle = '#ff9800';
    ctx.fillRect(75, h / 2 - 3, 10, 6);
    ctx.fillStyle = '#4fc3f7';
    ctx.fillRect(145, h / 2 - 15, 10, 6);

    ctx.fillStyle = '#94a3b8';
    ctx.font = '8px Inter';
    ctx.fillText('Jet', 72, h / 2 + 15);
    ctx.fillText('Bleed', 140, h / 2 - 18);
}

function drawRichLeanDiagram(ctx, w, h) {
    const gradient = ctx.createLinearGradient(20, 0, w - 20, 0);
    gradient.addColorStop(0, '#c62828');
    gradient.addColorStop(0.35, '#ff6600');
    gradient.addColorStop(0.5, '#ff9800');
    gradient.addColorStop(0.65, '#ffcc80');
    gradient.addColorStop(1, '#e3f2fd');

    ctx.fillStyle = gradient;
    ctx.fillRect(20, 25, w - 40, 20);

    ctx.fillStyle = '#94a3b8';
    ctx.font = '9px Inter';
    ctx.fillText('RICH', 20, 18);
    ctx.fillText('Best Power', w * 0.33, 60);
    ctx.fillText('Best Economy', w * 0.55, 60);
    ctx.fillText('LEAN', w - 50, 18);
    ctx.fillText('← Too Rich', 20, h - 5);
    ctx.fillText('Too Lean →', w - 80, h - 5);
}

function drawBleedDiagram(ctx, w, h) {
    ctx.fillStyle = '#94a3b8';
    ctx.font = '9px Inter';
    ctx.fillText('Fuel + Air Bleed → Emulsion', 10, 12);

    ctx.strokeStyle = '#c62828';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(30, h - 10);
    ctx.lineTo(30, 25);
    ctx.stroke();

    ctx.strokeStyle = '#4fc3f7';
    ctx.setLineDash([3, 3]);
    ctx.beginPath();
    ctx.moveTo(80, 25);
    ctx.lineTo(30, 35);
    ctx.stroke();
    ctx.setLineDash([]);

    ctx.strokeStyle = '#ff9800';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(30, 25);
    ctx.lineTo(150, 25);
    ctx.stroke();

    ctx.fillStyle = '#4fc3f7';
    ctx.font = '8px Inter';
    ctx.fillText('Air', 85, 22);
    ctx.fillStyle = '#c62828';
    ctx.fillText('Fuel', 10, h / 2);
    ctx.fillStyle = '#ff9800';
    ctx.fillText('Emulsion', 100, 40);
}
